import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchSavedReportById } from '@api/savedReports';
import { useReport } from '../../hooks/useReport';
import { useReportUrlFilters } from '../../hooks/useReportUrlFilters';
import { hasRequiredFiltersToConfig } from '../../utils/reportUtils';
import { convertTimeRangeToDashboardFilters } from '../../utils/timeRangeUtils';
import DashboardViewer from '../Dashboard/DashboardViewer';
import * as styles from './ReportViewPage.module.css';

function ReportViewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const savedReportIdParam = searchParams.get('saved_report_id');
  const [embedSavedReport, setEmbedSavedReport] = useState({
    loading: Boolean(savedReportIdParam),
    data: null,
    error: null,
  });
  const { report, loading } = useReport(id);

  useEffect(() => {
    if (!savedReportIdParam) {
      setEmbedSavedReport({ loading: false, data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setEmbedSavedReport({ loading: true, data: null, error: null });
    fetchSavedReportById(savedReportIdParam).then((result) => {
      if (cancelled) return;
      if (result.status !== 'success' || !result.data) {
        setEmbedSavedReport({
          loading: false,
          data: null,
          error: result.message || t('reports.errors.failedToLoadSavedReport'),
        });
        return;
      }
      if (String(result.data.dashboardId) !== String(id)) {
        setEmbedSavedReport({
          loading: false,
          data: null,
          error: t('reports.errors.savedReportDashboardMismatch'),
        });
        return;
      }
      setEmbedSavedReport({ loading: false, data: result.data, error: null });
    });
    return () => {
      cancelled = true;
    };
  }, [savedReportIdParam, id, t]);

  // Parse filters from URL query parameters (for external linking)
  const urlFilters = useReportUrlFilters();

  // Convert timeRange to timeFrom/timeTo format expected by DashboardViewer
  const queryParamFilters = useMemo(() => {
    if (!urlFilters) return null;

    // Convert timeRange object to timeFrom/timeTo if present
    if (urlFilters.timeRange) {
      const { timeFrom, timeTo } = convertTimeRangeToDashboardFilters(urlFilters.timeRange);
      return {
        ...urlFilters,
        timeFrom,
        timeTo,
      };
    }
    return urlFilters;
  }, [urlFilters]);

  // Get filters and edit mode state from location.state (passed from configure page)
  const locationState = useMemo(() => location.state || {}, [location.state]);
  const { 
    filters: stateFilters = null,
    isEditMode = false,
    savedReportId,
    savedReportName,
    savedReportDescription,
    savedReportConfiguration,
  } = locationState;

  // Priority: in-app navigation state > embed short URL (saved_report_id + API) > query params
  const initialFilters = useMemo(() => {
    if (stateFilters) return stateFilters;
    if (savedReportIdParam) {
      if (embedSavedReport.data?.configuration != null) {
        return embedSavedReport.data.configuration;
      }
      return null;
    }
    return queryParamFilters;
  }, [stateFilters, savedReportIdParam, embedSavedReport.data, queryParamFilters]);

  // Check if embed mode is enabled (from URL params)
  const embedMode = urlFilters?.embedMode || false;

  const resolvedSavedReportId = savedReportId || embedSavedReport.data?.id || null;
  const resolvedSavedReportName = savedReportName || embedSavedReport.data?.name || '';
  const resolvedSavedReportDescription = savedReportDescription || embedSavedReport.data?.description || '';

  // Determine if this is a saved report view (has savedReportId but not in edit mode)
  const isSavedReportView = Boolean(resolvedSavedReportId) && !isEditMode;

  const handleBack = useCallback(() => {
    if (isEditMode) {
      // Go back to configuration page with edit mode state
      // Pass the original saved report configuration, not the current filters
      navigate(`/${id}/configure`, {
        state: {
          savedReportId: resolvedSavedReportId,
          savedReportName: resolvedSavedReportName,
          savedReportDescription: resolvedSavedReportDescription,
          // Use the original saved report configuration for the "Back" navigation
          initialFilters: savedReportConfiguration || initialFilters,
          isEditMode: true,
        },
      });
    } else if (isSavedReportView) {
      // Viewing a saved report - go back to saved reports list
      navigate('/saved');
    } else if (report && hasRequiredFiltersToConfig(report)) {
      // Go back to configure page with the current filters preserved
      navigate(`/${id}/configure`, {
        state: {
          initialFilters,
        },
      });
    } else {
      navigate('/');
    }
  }, [
    report,
    id,
    navigate,
    isEditMode,
    isSavedReportView,
    resolvedSavedReportId,
    resolvedSavedReportName,
    resolvedSavedReportDescription,
    savedReportConfiguration,
    initialFilters,
  ]);

  // Show loading while report is being fetched (or saved report for embed short URL)
  if (loading || !report || (savedReportIdParam && embedSavedReport.loading)) {
    return null;
  }

  if (savedReportIdParam && embedSavedReport.error) {
    return (
      <div className={styles.errorMessage} role="alert">
        {embedSavedReport.error}
      </div>
    );
  }

  // If filters required but not provided, redirect to configure page
  if (hasRequiredFiltersToConfig(report) && !initialFilters) {
    return <Navigate to={`/${id}/configure`} replace />;
  }

  // Show saved report name if viewing or editing a saved report
  const showSavedReportName = isEditMode || isSavedReportView;

  return (
    <DashboardViewer
      dashboard={report}
      initialFilters={initialFilters}
      showFilters={false}
      onBack={handleBack}
      isEditMode={isEditMode}
      savedReportId={resolvedSavedReportId}
      savedReportName={showSavedReportName ? resolvedSavedReportName : ''}
      isSavedReportView={isSavedReportView}
      embedMode={embedMode}
    />
  );
}

export default ReportViewPage;
